
import { defineComponent } from 'vue';
import { $api } from '../common/apiService';
import { Trip } from '@/Models/Trip';
import { HomepageConfig } from '@/Models/HomepageConfig';
import HeroSection from '@/components/HeroSection.vue';

export default defineComponent({
  name: 'HomePage',
  components: { HeroSection },
  data() {
    return {
      config: null as HomepageConfig | null,
      pastTrip: null as Trip | null,
      futureTrip: null as Trip | null,
      heroImages: [] as string[],
      collageImages: [] as string[],
    };
  },
  async mounted() {
    this.config = await $api.homepageConfig.get();

    const token = this.$store.getters.getSasToken;
    const trips = await $api.trips.fetch();
    if (!trips || trips.length === 0) return;

    const today = new Date();
    const past = trips.filter(t => new Date(t.startDate) < today);
    const future = trips.filter(t => new Date(t.startDate) >= today);

    this.pastTrip = past[0] ?? null;
    this.futureTrip = future[0] ?? null;

    await Promise.all([
      this.loadHeroImages(token),
      this.loadCollageImages(token),
    ]);
  },
  methods: {
    async loadHeroImages(token: string) {
      const curatedIds = this.config?.frontpageImageIds ?? [];

      if (curatedIds.length > 0) {
        const fetched = await Promise.all(curatedIds.map(id => $api.images.get(id)));
        this.heroImages = fetched
          .filter(img => img !== null)
          .map(img => img!.uri + '?' + token);
      } else {
        if (!this.pastTrip) return;
        const pt = this.pastTrip;

        // Hero uses only gallery images — cover + location are reserved for the collage
        const gallery = await $api.images.fetch(pt.year);
        if (gallery) {
          this.heroImages = gallery
            .filter(img => img.id !== pt.coverImageId && img.id !== pt.locationImageId)
            .slice(0, 5)
            .map(img => img.uri + '?' + token);
        }
      }

    },

    async loadCollageImages(token: string) {
      if (!this.pastTrip) return;
      const pt = this.pastTrip;
      const images: string[] = [];

      // Collage uses the trip's curated images (cover + location) — always different from hero
      if (pt.coverImageId) {
        const img = await $api.images.get(pt.coverImageId);
        if (img) images.push((img.thumbUri || img.uri) + '?' + token);
      }
      if (pt.locationImageId && images.length < 3) {
        const img = await $api.images.get(pt.locationImageId);
        if (img) images.push((img.thumbUri || img.uri) + '?' + token);
      }

      // Fill remaining slots from gallery if needed
      if (images.length < 3) {
        const curatedIds = new Set(this.config?.frontpageImageIds ?? []);
        const gallery = await $api.images.fetch(pt.year);
        if (gallery) {
          const remaining = gallery
            .filter(img => img.id !== pt.coverImageId && img.id !== pt.locationImageId)
            .filter(img => !curatedIds.has(img.id));
          images.push(
            ...remaining
              .slice(0, 3 - images.length)
              .map(img => (img.thumbUri || img.uri) + '?' + token)
          );
        }
      }

      this.collageImages = images;
    },

    formatDateRange(startDate: string, endDate: string): string {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
      const s = start.toLocaleDateString('da-DK', opts);
      const e = end.toLocaleDateString('da-DK', { ...opts, year: 'numeric' });
      return `${s} – ${e}`;
    },
  },
});
